import {Trans, useLingui} from "@lingui/react/macro";
import {useRef, useState} from "react";
import {Navigate, useParams} from "react-router";
import type {UserChallengeResponse} from "../../features/auth/client-auth";
import {acceptDeviceLink} from "../../features/auth/client-auth";
import type {UserRepo} from "../../models/UserRepo";
import {syncUserRepo} from "../../models/UserRepo";
import AppShell from "../../shared/ui/AppShell";
import Spinner from "../../shared/ui/Spinner";
import {Col, Text} from "../../ui/Box";
import Button from "../../ui/Button";

const AcceptButton = ({secret, userRepo}: {secret: string; userRepo: UserRepo}) => {
  const [info, setInfo] = useState<null | UserChallengeResponse>(null);
  const {t} = useLingui();
  const handleAccept = async () => {
    const val = await acceptDeviceLink(secret);
    setInfo(val);
    if (val.ok) await syncUserRepo(val.userId, userRepo);
  };
  if (!info) {
    return (
      <Col rounded="4" surface pa="16" sp="12" align="center">
        <Button onClick={handleAccept}>
          <Trans>Share data with this device</Trans>
        </Button>
      </Col>
    );
  } else if (info.ok) {
    return <Spinner size="48" />;
  } else {
    const getErrorMessage = () => {
      switch (info.errorKey) {
        case "notFound":
          return t`Link not valid`;
        case "expired":
          return t`Invite link expired`;
        default:
          return info.errorKey;
      }
    };
    return (
      <Col rounded="4" surface pa="16" sp="12" align="center">
        <Text as="h2" size="20">
          {getErrorMessage()}
        </Text>
        <Text>
          <Trans>Please try again.</Trans>
        </Text>
      </Col>
    );
  }
};

const SyncUser = ({userRepo}: {userRepo: UserRepo}) => {
  const {id} = userRepo.vals.useValues();
  const initialIdRef = useRef(id);
  const {secret} = useParams<{secret: string}>();
  return (
    <AppShell>
      <Col px="12" sp="24" py="24">
        <Text as="h1">
          <Trans>Device Sharing</Trans>
        </Text>
        {initialIdRef.current ? (
          <Text rounded="4" surface pa="16">
            <Trans>
              You already have an account on this device. If you want to remove the account from
              this device, open your browser settings and delete all data for this page.
            </Trans>
          </Text>
        ) : id ? (
          <Navigate to="/" />
        ) : (
          <AcceptButton secret={secret || ""} userRepo={userRepo} />
        )}
      </Col>
    </AppShell>
  );
};

export default SyncUser;
